import { useCallback, useEffect, useState } from 'react';
import { AdviseFeatureInput, AdviseFeatureResponse } from '../../types/advise';
import { isNotFoundRefusal, refusalMessage } from '../../utils/refusalMessage';
import { fetchAdviseFeatureInput, updateAdviseFeatureResponseFlag } from './FeatureInput';

const FLAG_FAILED = "Couldn't change the answer's flag. Please try again.";
const ANSWER_REPLACED = 'That answer was replaced by a newer import, so the answers have been reloaded.';

export interface FlagNotice {
  message: string;
  severity: 'error' | 'info';
}

const flagFailed = (message: string): FlagNotice => ({ message, severity: 'error' });

/** The shortlist's requirement sheets and imported answers; null until loaded. A reload keeps showing the last load. */
export const useEngagementFeatureInput = (engagementId: string | null) => {
  const [featureInput, setFeatureInput] = useState<AdviseFeatureInput | null>(null);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    setFeatureInput(null);
  }, [engagementId]);

  useEffect(() => {
    setFailed(false);
    if (!engagementId) return;
    let current = true;
    const load = async () => {
      const { data, error } = await fetchAdviseFeatureInput(engagementId);
      if (!current) return;
      if (error || !data) setFailed(true);
      else setFeatureInput(data);
    };
    load();
    return () => {
      current = false;
    };
  }, [engagementId, attempt]);

  const reload = useCallback(() => setAttempt((count) => count + 1), []);

  // Flags the answer, or clears its flag; resolves to what to tell the Analyst, or null once the change shows.
  const setFlag = useCallback(
    async (response: AdviseFeatureResponse, flagged: boolean): Promise<FlagNotice | null> => {
      if (!engagementId) return flagFailed(FLAG_FAILED);
      const { data, error } = await updateAdviseFeatureResponseFlag(engagementId, response.id, flagged);
      if (isNotFoundRefusal(error)) {
        reload();
        return { message: ANSWER_REPLACED, severity: 'info' };
      }
      if (error || !data) return flagFailed(refusalMessage(error, FLAG_FAILED));
      setFeatureInput(
        (prev) => prev && { ...prev, responses: prev.responses.map((row) => (row.id === data.id ? data : row)) }
      );
      return null;
    },
    [engagementId, reload]
  );

  return { featureInput, failed, reload, setFlag };
};
